import type { DateRangeValue } from "@/components/ui/date-range-picker";
import { lastMonthsRange } from "@/lib/date-utils";
import type { ResourceStatus, ResourceType } from "@/types/admin";
import { createTableFiltersStore } from "./createTableFiltersStore";

/**
 * Options for the resource list's Type filter. Lives here rather than in the page because the
 * store's `sanitize` validates a restored value against it.
 */
export const RESOURCE_TYPE_OPTIONS: {
  value: ResourceType | "All";
  label: string;
}[] = [
  { value: "All", label: "All Types" },
  { value: "VirtualMachine", label: "Virtual Machine" },
  { value: "Storage", label: "Storage" },
  { value: "Network", label: "Network" },
  { value: "Database", label: "Database" },
  { value: "LoadBalancer", label: "Load Balancer" },
  { value: "ObjectStorage", label: "Object Storage" },
];

/** Options for the resource list's Status filter. Same reason as above for living here. */
export const RESOURCE_STATUS_OPTIONS: {
  value: ResourceStatus | "All";
  label: string;
}[] = [
  { value: "All", label: "All Statuses" },
  { value: "Provisioning", label: "Provisioning" },
  { value: "Pending", label: "Pending" },
  { value: "Running", label: "Running" },
  { value: "Stopped", label: "Stopped" },
  { value: "Suspended", label: "Suspended" },
  { value: "Error", label: "Error" },
  { value: "Terminating", label: "Terminating" },
  { value: "Terminated", label: "Terminated" },
  { value: "Decommissioning", label: "Decommissioning" },
];

/**
 * The resource list's own Status options: "Active" first, the default (2026-10-14) — live rows that
 * are not Terminated, what an operator comes to this screen for — then every status. "All Statuses"
 * here lists every row, deleted ones included, and a picked status is matched on deleted rows too.
 * `RESOURCE_STATUS_OPTIONS` stays as it is for the product-line audit, which has no "Active".
 */
export const RESOURCE_LIST_STATUS_OPTIONS: {
  value: ResourceStatus | "All" | "Active";
  label: string;
}[] = [
  { value: "Active", label: "Active (hide deleted & Terminated)" },
  ...RESOURCE_STATUS_OPTIONS,
];

/**
 * The "expiring soon" windows the screen offers, in days from now. `null` is the cleared state.
 *
 * Two short windows rather than a free-form number: this filter answers one question — "what do I
 * have to warn a customer about today?" — and three days is the shortest notice worth acting on,
 * a week the longest that still reads as urgent.
 */
export const RESOURCE_EXPIRING_OPTIONS: {
  value: number | null;
  label: string;
}[] = [
  { value: null, label: "Any expiry date" },
  { value: 3, label: "Expiring in 3 days" },
  { value: 7, label: "Expiring in 7 days" },
];

/**
 * The Product Line filter's "no filter" value. The lines themselves are not listed here: the page
 * reads them from the catalogue, because a slug is what a machine records and what the backend
 * filters on, and a slug can be renamed — or a line added — in the Product Lines screen. A list
 * fixed in code would stop offering the lines new machines are created on.
 */
export const ALL_PRODUCT_LINES = "All";

/** Longest product-line slug the endpoint accepts — mirrors the backend `GetResourcesQueryValidator`. */
const MAX_PRODUCT_LINE_SLUG_LENGTH = 100;

/** True for a stored product-line value the endpoint would accept: a non-blank string within the limit. */
const isUsableProductLineSlug = (slug: unknown): slug is string =>
  typeof slug === "string" &&
  slug.trim() !== "" &&
  slug.length <= MAX_PRODUCT_LINE_SLUG_LENGTH;

export interface ResourceListFilters extends Record<string, unknown> {
  /** Free-text term: VM name, VM uuid, or the owner's email / phone. */
  search: string;

  type: ResourceType | "All";

  status: ResourceStatus | "All" | "Active";

  /** Created-at range, ISO strings. Both ends or neither — the API ignores a half range. */
  dateRange: DateRangeValue;

  /**
   * Days-from-now window on the expiry date, or null for no expiry filter. Picking one also
   * narrows Type to virtual machines and clears the created-at range — see the page, which owns
   * that rule, and `RESOURCE_EXPIRING_OPTIONS` for why the windows are short.
   */
  expiringInDays: number | null;

  /**
   * The slug of the product line the machine was ordered on, or `ALL_PRODUCT_LINES` for no filter.
   * It matches the line the Type column shows under a machine, so one with none recorded is found
   * under no line. Picking one also narrows Type to virtual machines — see the page, which owns
   * that rule.
   */
  productLineSlug: string;
}

/**
 * True when a stored range has exactly one end. The picker only ever produces both or neither, so
 * this can only come from hand-edited storage.
 */
const isHalfRange = (range: DateRangeValue | undefined): boolean =>
  Boolean(range?.from) !== Boolean(range?.to);

/**
 * The list opens on virtual machines rather than on every type. A VM is the row an operator comes
 * to this screen for; its disk and network rows are parts of that same order and pad the table to
 * roughly three times its useful length, pushing the VMs apart and across pages. Widening to a
 * single other type — or back to all of them — is one click on the Type filter, and the default is
 * visible on screen as a clearable chip, so nothing is being hidden.
 *
 * Both ends of the created-at range default to the last six months rather than to "no filter", for
 * the same reason: the list opens on a bounded window, and the operator widens or clears it from
 * the picker. Computed once per page load, so a tab left open for days keeps the window it started
 * with until the filters are reset.
 */
const defaults: ResourceListFilters = {
  search: "",
  type: "VirtualMachine",
  status: "Active",
  dateRange: lastMonthsRange(),
  expiringInDays: null,
  productLineSlug: ALL_PRODUCT_LINES,
};

/**
 * Filter and paging state for the admin resource list, cached in sessionStorage.
 *
 * Bump `version` in step with any change to the shape above, to the option lists, or to a
 * default. v5 dropped `tenantId`: its input is gone from the toolbar (a GUID typed into the main
 * search box now matches a tenant), and a value left in localStorage would have gone on filtering
 * the list with no control anywhere to clear it — a stored status the backend no longer accepts comes back as a 400 on a filter the
 * operator cannot see, and a cached value equal to the *old* default would otherwise keep an
 * existing operator on it forever, since nothing distinguishes it from a deliberate choice. v6
 * added `deletion` — a value from an even later release restored here would otherwise never match
 * `RESOURCE_DELETION_OPTIONS` and fall back to the default anyway, but the bump documents the shape
 * change rather than leaving it silent. v7 added `productLineSlug`, for the same reason. v8 (2026-10-14)
 * retired `deletion` — a cached "All deleted" would otherwise keep sending a filter nothing on
 * screen can clear — and moved the Status default from "All" to "Active": a cached "All", the old
 * default, would otherwise now mean "every row, deleted ones included".
 */
export const useResourceFiltersStore =
  createTableFiltersStore<ResourceListFilters>({
    name: "admin.resources.filters",
    version: 8,
    defaults,
    sanitize: (filters) => ({
      ...filters,

      // sessionStorage outlives a release. An option removed from either list must not survive in a
      // restored filter, or the operator sees an empty table filtered by something they cannot see
      // in the UI and cannot clear from it.
      type: RESOURCE_TYPE_OPTIONS.some(
        (option) => option.value === filters.type,
      )
        ? filters.type
        : defaults.type,
      status: RESOURCE_LIST_STATUS_OPTIONS.some(
        (option) => option.value === filters.status,
      )
        ? filters.status
        : defaults.status,

      // Unlike the lists above, the lines on offer come from the catalogue at runtime, so a restored
      // slug cannot be checked against them here. One the catalogue no longer names still shows as a
      // chip the operator can clear; only a value the endpoint would refuse is dropped.
      productLineSlug: isUsableProductLineSlug(filters.productLineSlug)
        ? filters.productLineSlug
        : defaults.productLineSlug,

      // A window no longer offered - or a hand-edited one the endpoint would reject with a 400 on a
      // filter the operator cannot see - falls back to no expiry filter.
      expiringInDays: RESOURCE_EXPIRING_OPTIONS.some(
        (option) => option.value === filters.expiringInDays,
      )
        ? filters.expiringInDays
        : defaults.expiringInDays,

      // Exactly one end is not a range: the query applies createdFrom/createdTo together, so a
      // stored half would show as a filter chip that narrows nothing. An *empty* range is left alone —
      // it is the operator having deliberately cleared the six-month default to search all time, and
      // replacing it here would silently put the default back on every reload.
      dateRange: isHalfRange(filters.dateRange)
        ? defaults.dateRange
        : filters.dateRange,
    }),
  });
